import { OrderData } from '../../queries/shopify/getOrderData';
import { CustomAttribute } from '../../models/customAttribute';
import { Metafield } from '../../models/metafield';
import moment from 'moment';
import {
  LIVEART_CUSTOM_DESIGN,
  MANUFACTURER_OPTIONS,
  META_SHIP_DATE_KEY,
  META_STATUS_KEY,
  Manufacturer,
} from '../types';
import { MetafieldOrderStatus, MetafieldShipDate, Order } from '../../models/order';
import { ManufacturerStatus, OrderStatus } from '../../models/orderStatus';
import { ShipDate } from '../../models/shipDate';
import { SendStatus } from '../../models/sendStatus';
import { Item } from '../../models/item';

/**
 * Map response from `getOrderData` request to Order object
 * @param orderData
 */
export function mapOrderDataToObject(orderData: OrderData): Order {
  const sendManStatus = getOrderStatus(orderData);
  return {
    id: orderData.id,
    name: orderData.name,
    customer: getOrderCustomer(orderData),
    totalPrice: getOrderTotal(orderData),
    sendManStatus,
    sendManShipDate: getOrderShipDate(orderData),
    items: getOrderLAItems(orderData, isMetafieldOrderStatus(sendManStatus) ? sendManStatus : undefined),
    originalProducts: [],
    ...getOrderTagsAndAttributes(orderData),
  };
}

export function isMetafieldOrderStatus(status: OrderStatus | MetafieldOrderStatus): status is MetafieldOrderStatus {
  return 'value' in status;
}

/**
 * Get parsed value of Status metafield of Order if it exists
 * @param orderData
 * @return { {id: string, key: string, value: *} | {key:{"status": "not-sent"}} }
 */
export function getOrderStatus(orderData: OrderData): OrderStatus | MetafieldOrderStatus {
  const defaultMeta: Partial<OrderStatus> = {};
  MANUFACTURER_OPTIONS.forEach(man => {
    defaultMeta[man] = { status: SendStatus.notSent };
  });
  const metafields = getMetafields(orderData);
  if (!metafields) return defaultMeta as OrderStatus;

  const statusMeta = metafields.find(m => m.key === META_STATUS_KEY);
  return statusMeta
    ? {
      ...statusMeta,
      value: JSON.parse(statusMeta.value) || {},
    }
    : defaultMeta as OrderStatus;
}

/**
 * Get parsed value of Delivery Date metafield of Order if it exists
 * @param orderData
 * @return {{id: string, key: string, value: *}|null}
 */
export function getOrderShipDate(orderData: OrderData): MetafieldShipDate | null {
  const metafields = getMetafields(orderData);
  if (!metafields) return null;

  const deliveryDateMeta = metafields.find((m: Metafield) => m.key === META_SHIP_DATE_KEY);
  return deliveryDateMeta
    ? {
      ...deliveryDateMeta,
      value:
        normalizeShipDateObjectDate(
          JSON.parse(deliveryDateMeta.value) as ShipDate,
          true,
        ) || {},
    }
    : null;
}

export function getOrderTagsAndAttributes(orderData: OrderData) {
  return {
    tags: orderData.tags,
    customAttributes: orderData.customAttributes
      // strip attr.__typename property
      .map((attr: CustomAttribute) => ({
        key: attr.key,
        value: attr.value,
      })),
  };
}

export function getOrderLAItems(orderData: OrderData, orderStatus?: MetafieldOrderStatus): Item[] {
  const actualVendors = Object.entries(orderStatus?.value ?? {})
    .reduce(
      (acc: Partial<Record<string, Manufacturer>>, [vendor, status]: [Manufacturer, ManufacturerStatus]) => {
        status?.lineItems?.forEach(item => acc[item] = vendor); return acc;
      }, {}
    );
  const items: Item[] = [];

  for (const iterator of orderData.lineItems.edges) {
    const lineItem = iterator.node;
    if (!lineItem.product) continue; // skip line items without product (e.g. tips)
    const actualVendor = actualVendors[lineItem.id] ?? lineItem.product.vendor;

    if (lineItem.product.productType === LIVEART_CUSTOM_DESIGN) {
      items.push({
        type: 'custom',
        ...lineItem,
        product: {
          ...lineItem.product,
          vendor: actualVendor,
        }
      });
    } else if (lineItem.customAttributes.find(({ key }) => key === 'Preorder')) {
      items.push({
        type: 'preorder',
        ...lineItem,
        product: {
          ...lineItem.product,
          garmentId: lineItem.product.id,
          vendor: actualVendor,
        }
      });
    }
  }
  return items;
}

function getOrderCustomer(orderData: OrderData) {
  return {
    firstName: orderData.customer ? orderData.customer.firstName : '',
    email:
      orderData.customer && orderData.customer.validEmailAddress
        ? orderData.customer.email
        : '',
  };
}

function getOrderTotal(orderData: OrderData) {
  if (!orderData.totalPriceSet) return null;
  const price = orderData.totalPriceSet.shopMoney;
  return price.currencyCode + price.amount;
}

function getMetafields(orderData: OrderData) {
  if (
    !orderData.metafields ||
    !orderData.metafields.edges ||
    !orderData.metafields.edges.length
  )
    return null;
  return orderData.metafields.edges.map(({ node }) => node);
}

/**
 * Add/subtract timezone offset from given value to write/read
 * date value to/from metafield in current timezone's value
 * @param object
 * @param fromUTC - if `object.date` is in UTC
 */
export function normalizeShipDateObjectDate(object: ShipDate, fromUTC = false) {
  if (!object || !object.date) return object;

  const offsetMinutes = new Date().getTimezoneOffset();
  let date = moment(object.date);
  if (fromUTC) date = date.add(offsetMinutes, 'minutes');
  else date = date.subtract(offsetMinutes, 'minutes');

  return {
    ...object,
    date: date.toDate(),
  };
}
